import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { FieldExtractionStatusResponse, Task } from './types';
import { TaskNames } from 'src/utils/constants/task-constants';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface GetFieldExtractionStatusParams {
  loanId: string;
  // Not used by the request itself (the endpoint has no document filter) —
  // included only so the React Query cache key is scoped per document,
  // preventing a previously-viewed document's cached status from leaking
  // into a newly opened one.
  documentId?: string;
}

const getFieldExtractionStatus: QueryFunction<
  string,
  ['/field-extraction-status', GetFieldExtractionStatusParams]
> = async ({ queryKey }) => {
  if (isMockServices()) {
    // Mimic the same logic as in the real response for the mock
    const data = await mock(_mockData);
    if (data.success) {
      // Use a typed sort to avoid mutation and SonarQube warning
      const orderByLatestTask = [...data.tasks].sort(
        (a: Task, b: Task) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      if (orderByLatestTask.length === 0) return 'completed';
      const latestTask = orderByLatestTask[0];
      return latestTask.status;
    }
    return 'completed';
  }

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<FieldExtractionStatusResponse>(
    `/tasks?loanId=${queryKey[1].loanId}&serviceName=${TaskNames.EXTRACTOR}`,
    {
      method: 'GET',
      paginate: true,
      accumulateKey: 'tasks',
      nextCursorKey: 'nextCursor',
      limit: 100,
    },
  );
  if (response.data) {
    // Flatten all tasks from each page
    const allTasks: Task[] = response.data.tasks;

    // Use a typed sort to avoid mutation and SonarQube warning
    const orderByLatestTask = [...allTasks].sort(
      (a: Task, b: Task) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    if (orderByLatestTask.length === 0) return 'completed'; // No tasks found, return completed status
    const latestTask = orderByLatestTask[0];
    return latestTask.status;
  }
  throw new Error('Failed to fetch field extraction status');
};

export default getFieldExtractionStatus;

export const _mockData: FieldExtractionStatusResponse = {
  success: true,
  tasks: [
    {
      id: 'task-1',
      loanId: 'nsmabqhy69xhe0qvzrfe8cbr',
      serviceName: TaskNames.EXTRACTOR,
      status: 'completed',
      createdAt: '2023-10-01T12:00:00Z',
    },
    {
      id: 'task-2',
      loanId: 'nsmabqhy69xhe0qvzrfe8cbr',
      serviceName: TaskNames.EXTRACTOR,
      status: 'running',
      createdAt: '2023-10-02T12:02:00Z',
    },
  ],
};
